import { z } from 'zod';

/**
 * Columns of the student import template, in template order. `header` is the French column title (the
 * template is a French administrative file, like the grade template); the parser also accepts it without
 * accents, in any case and order. Only `lastName`, `firstName` and `level` are required.
 */
export const STUDENT_IMPORT_COLUMNS = [
  { key: 'matricule', header: 'Matricule' },
  { key: 'lastName', header: 'Nom' },
  { key: 'firstName', header: 'Prénom' },
  { key: 'lastNameAr', header: 'Nom (arabe)' },
  { key: 'firstNameAr', header: 'Prénom (arabe)' },
  { key: 'gender', header: 'Sexe' },
  { key: 'cin', header: 'CIN' },
  { key: 'birthDate', header: 'Date de naissance' },
  { key: 'birthPlace', header: 'Lieu de naissance' },
  { key: 'nationality', header: 'Nationalité' },
  { key: 'email', header: 'E-mail' },
  { key: 'phone', header: 'Téléphone' },
  { key: 'address', header: 'Adresse' },
  { key: 'city', header: 'Ville' },
  { key: 'bacSection', header: 'Section bac' },
  { key: 'bacYear', header: 'Année bac' },
  { key: 'bacAverage', header: 'Moyenne bac' },
  { key: 'bacSchool', header: 'Lycée' },
  { key: 'guardianName', header: 'Tuteur' },
  { key: 'guardianRelation', header: 'Lien tuteur' },
  { key: 'guardianPhone', header: 'Téléphone tuteur' },
  { key: 'level', header: 'Niveau' },
  { key: 'group', header: 'Groupe' },
] as const;
export type StudentImportColumn = (typeof STUDENT_IMPORT_COLUMNS)[number]['key'];

/**
 * Outcome of one row. Preview: what « Importer » would do; applied: what was done.
 * create = new record + enrollment; enroll = existing student (matricule or CIN) enrolled for the year;
 * skip = existing student already enrolled for the year (records are never overwritten); error = not imported.
 */
export const STUDENT_IMPORT_OUTCOMES = ['create', 'enroll', 'skip', 'error'] as const;
export type StudentImportOutcome = (typeof STUDENT_IMPORT_OUTCOMES)[number];

export const StudentImportRowSchema = z.object({
  row: z.number().int(),
  name: z.string(),
  outcome: z.enum(STUDENT_IMPORT_OUTCOMES),
  /** Existing student, or the matricule assigned once applied. */
  matricule: z.string().nullable(),
  /** i18n codes (`students.import.rowErrors.<code>`), with the column concerned; `ref` = a matricule. */
  errors: z.array(z.object({ column: z.string(), code: z.string(), ref: z.string().optional() })),
  /** Imported anyway, to check: `homonym` = a student with the same name exists (`ref`: their matricule). */
  warnings: z.array(z.object({ code: z.literal('homonym'), ref: z.string() })),
});
export type StudentImportRow = z.infer<typeof StudentImportRowSchema>;

export const StudentImportReportSchema = z.object({
  rows: z.number().int(),
  create: z.number().int(),
  enroll: z.number().int(),
  skip: z.number().int(),
  error: z.number().int(),
  /** Rows in file order (capped at 2 000 — the file limit). */
  items: z.array(StudentImportRowSchema),
  applied: z.boolean(),
  /** Access e-mails queued (apply with « Envoyer l'accès »). */
  invited: z.number().int(),
});
export type StudentImportReport = z.infer<typeof StudentImportReportSchema>;

export const StudentImportSchema = z.object({
  id: z.uuid(),
  fileName: z.string(),
  academicYearId: z.uuid(),
  apply: z.boolean(),
  invite: z.boolean(),
  status: z.enum(['pending', 'done', 'failed']),
  report: StudentImportReportSchema.nullable(),
  /** i18n reason: file | columns | empty | too_many | year_closed | internal */
  error: z.string().nullable(),
});
export type StudentImport = z.infer<typeof StudentImportSchema>;

export const StudentImportUploadQuerySchema = z.object({ yearId: z.uuid() });
export const StudentImportApplySchema = z.object({ invite: z.boolean().default(false) });
export type StudentImportApply = z.input<typeof StudentImportApplySchema>;

/** « Envoyer l'accès » : e-mail with the login (matricule) and a link to choose a password. */
export const SendStudentAccessSchema = z.object({ studentIds: z.array(z.uuid()).min(1).max(500) });
export type SendStudentAccess = z.infer<typeof SendStudentAccessSchema>;
export const SendStudentAccessResultSchema = z.object({
  sent: z.number().int(),
  /** Selected students without an e-mail address (nothing sent). */
  withoutEmail: z.number().int(),
});
export type SendStudentAccessResult = z.infer<typeof SendStudentAccessResultSchema>;

/** Days an access link stays valid (same as the administrator invite). */
export const STUDENT_ACCESS_TTL_DAYS = 7;
